import React from 'react';
import Link from 'next/link';
import { Container } from 'components/base';
import { WIDTH_LADDERS, getCloudinaryUrl } from 'components/rich';

import { Picture, ctaTracker, landingImage, productImage } from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';

// About page (docs/about-page.md). Copy follows the Pencil frames "About — Redesign" /
// "About — Mobile". Company facts must match the Organization JSON-LD in components/layout/head.js.
const track = ctaTracker('about');

const HREF = {
  SHOP: '/shop',
  IDEAS: '/photo-wall-ideas',
};

const HERO = { imageKey: 'hero/about' };
// The hero/ folder has no width ladder, only the 800 px default and its @2x (1600 px). Width
// descriptors let a 1440 px screen at DPR 1 take the 1600 px file instead of the blurry 1x.
const HERO_URL = getCloudinaryUrl(HERO);
const HERO_SRC_SET = `${HERO_URL} 800w, ${HERO_URL.replace(/\.webp$/, '@2x.webp')} 1600w`;
const HERO_SIZES = '100vw';

const IMAGES = {
  paper: landingImage('hc-pages-macro'),
  rope: productImage('photo-ropes/1'),
  wall: {
    imageKey: 'landing/year-in-photos/engineer-print-30-grid-living-room',
    widths: WIDTH_LADDERS.landing,
  },
};

const BELIEFS = [
  {
    title: 'The right paper',
    body: 'Thick matte for Square and Classic Prints, extra-thick 308 GSM for Fine Art Prints, extra-light 20 lb paper for Engineer Prints so they hang without a frame.',
  },
  {
    title: 'Simple on purpose',
    body: 'A few formats and layouts that look good from the start. No clip art, no hours in an editor. Order from your phone in minutes.',
  },
  {
    title: 'Off the phone, onto the wall',
    body: 'Wood Poster Rails in oak, maple and black, the oak Wood Photo Block and the magnetic Photo Rope. Your prints go up, not into a drawer.',
  },
  {
    title: 'Made in the US, shipped internationally',
    body: 'Printed in Oregon, Pennsylvania and Utah and shipped to the US and abroad. Matte prints, books, calendars and cards use papers certified by the Rainforest Alliance for FSC standards.',
  },
];

const FACTS = [
  { value: 'Since 2015', label: 'Printing photos' },
  { value: 'American company', label: 'Based in Philadelphia, PA' },
  { value: 'Made in the US', label: 'Oregon, Pennsylvania and Utah' },
  { value: 'Ships abroad', label: 'US and international delivery' },
];

const AboutPage = () => (
  <>
    <Head
      title="About Our Photo Printing Company"
      description="Parabo Press is an American photo printing company, printing since 2015. Our prints, books and décor are made in Oregon, Pennsylvania and Utah."
      image={getCloudinaryUrl({ ...HERO, format: 'jpg' })}
      heroImage={HERO_URL}
      heroImageSrcSet={HERO_SRC_SET}
      heroImageSizes={HERO_SIZES}
    />

    <Layout>
      <div className="lnd ab">
        <section className="ab-hero">
          <Container>
            <p className="ab-eyebrow">About Parabo Press</p>
            <div className="ab-hero-row">
              <h1 className="ab-h1">
                From your camera roll <br className="ab-desk-break" />
                to your favorite wall.
              </h1>
              <p className="ab-lead">
                Parabo Press is an American photo printing company. We turn the photos on your phone
                into prints, books and wall pieces made to be touched, held and hung.
              </p>
            </div>
          </Container>
        </section>

        <div className="ab-hero-photo">
          {/* CloudinaryPicture only renders the DPR pair for this folder, see HERO_SRC_SET. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={HERO_URL}
            srcSet={HERO_SRC_SET}
            sizes={HERO_SIZES}
            alt="An Engineer Print of a coastal photo hangs from black Wood Poster Rails above an entryway bench"
            fetchpriority="high"
          />
        </div>

        <section className="ab-mission">
          <Container>
            <span className="ab-rule" aria-hidden="true" />
            <p className="ab-eyebrow">Our mission</p>
            <p className="ab-statement">
              We share in your joy, celebrate your story and help you hold onto life’s moments with
              thoughtfully designed photo prints.
            </p>
          </Container>
        </section>

        <section className="ab-story">
          <Container>
            <div className="ab-story-head">
              <p className="ab-eyebrow">Our story</p>
              <h2 className="lnd-h2 ab-h2">
                An American company, <br className="ab-desk-break" />
                in love with print.
              </h2>
            </div>
            <div className="ab-story-body">
              <p>
                We have always loved taking photos of life’s moments, big and small. But after
                filling our phones with birthday parties, family trips and countless smiles, we
                realized that taking the photos wasn’t the point. We wanted to hold on to them and
                enjoy them in real life.
              </p>
              <p>
                So in 2015 we started Parabo Press. Today we are based in Philadelphia, and every
                order is printed in Oregon, Pennsylvania or Utah.
              </p>
              <p>
                We still love the part that happens off the screen: the weight of a matte print, the
                snap of a magnetic rail, a book that falls open on the right page. That is why we
                keep the line short, with a few formats, each on a paper that suits it.
              </p>
            </div>
          </Container>
        </section>

        <section className="ab-care">
          <Container className="ab-care-inner">
            <div className="ab-care-head">
              <p className="ab-eyebrow">What we care about</p>
              <h2 className="lnd-h2 ab-h2">Made to be touched, held and hung.</h2>
            </div>
            <div className="ab-photos">
              <div className="ab-photo ab-photo--paper">
                <Picture
                  image={IMAGES.paper}
                  sizes="(max-width: 767px) 50vw, (max-width: 1279px) 45vw, 400px"
                  alt="Close-up of the thick pages of an open hardcover photo book"
                />
              </div>
              <div className="ab-photo ab-photo--rope">
                <Picture
                  image={IMAGES.rope}
                  sizes="(max-width: 767px) 50vw, (max-width: 1279px) 45vw, 320px"
                  alt="A woman hangs square prints on a magnetic Photo Rope"
                />
              </div>
            </div>
            <ol className="ab-beliefs">
              {BELIEFS.map((belief, index) => (
                <li key={belief.title} className="ab-belief">
                  <span className="ab-belief-number">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="ab-belief-title">{belief.title}</h3>
                    <p className="ab-belief-body">{belief.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Container>
        </section>

        <section className="ab-facts" aria-label="Parabo Press at a glance">
          <Container>
            <ul>
              {FACTS.map((fact) => (
                <li key={fact.value} className="ab-fact">
                  <p className="ab-fact-value">{fact.value}</p>
                  <p className="ab-fact-label">{fact.label}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="ab-cta">
          <div className="ab-cta-photo">
            <Picture
              image={IMAGES.wall}
              sizes="(max-width: 991px) 100vw, 50vw"
              alt="An Engineer Print with a grid of 30 photos hangs from wood Poster Rails in a living room"
            />
          </div>
          <div className="ab-cta-copy">
            <h2 className="ab-cta-title">
              Stop scrolling your memories. <br className="ab-desk-break" />
              Start displaying them.
            </h2>
            <p className="ab-cta-lead">
              Start with one photo you love or twenty you keep scrolling past.
            </p>
            <div className="ab-cta-actions">
              <Link
                href={HREF.SHOP}
                className="lnd-btn"
                onClick={track('shop', 'closing', HREF.SHOP)}
              >
                Shop all products
              </Link>
              <Link
                href={HREF.IDEAS}
                className="lnd-arrow-link ab-arrow-link"
                onClick={track('ideas', 'closing', HREF.IDEAS)}
              >
                See photo wall ideas
                <PdpIcon name="arrowRight" size={16} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  </>
);

export default AboutPage;
